/**
 * Shared Utilities - Moon Moon Agency
 * Essential utilities and helpers shared across all modules
 */

/*********************************************************************************************
 * 🌐 GLOBAL SETTINGS
 */
// Development mode detection (self-contained)
window.isDev = ['localhost', '127.0.0.1'].includes(location.hostname)
	|| location.hostname.startsWith('192.168.')
	|| location.hostname.startsWith('10.0.')
	|| location.hostname.endsWith('.myshopify.com');

// Breakpoints (synced with CSS — desktop-first)
window.WIDE_BREAKPOINT = 1440;
window.MEDIUM_DESKTOP_BREAKPOINT = 1280;
window.SMALL_DESKTOP_BREAKPOINT = 1024;
window.TABLET_BREAKPOINT = 990;
window.MOBILE_BREAKPOINT = 768;

/*********************************************************************************************
 * 🛠️ SHARED UTILITIES
 */
// Simple logging utility
window.log = (level, module, message, data = {}) => {
	if (!window.isDev && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		const timestamp = window.getTimestamp?.() || new Date().toLocaleString();
		logFn(`${module} ${message}`, { timestamp, ...data });
	}
};

// Simple event emitter
window.emit = (event, detail = {}) => {
	return document.dispatchEvent(new CustomEvent(event, {
		detail: { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...detail },
		bubbles: true
	}));
};

// DOM utilities
window.getElement = (selector) => document.querySelector(selector);
window.getElements = (selector) => document.querySelectorAll(selector);

// Device detection (use these instead of hardcoded breakpoints in modules)
window.isMobile = () => window.innerWidth <= window.MOBILE_BREAKPOINT;
window.isTablet = () => window.innerWidth <= window.TABLET_BREAKPOINT;
window.isSmallDesktop = () => window.innerWidth <= window.SMALL_DESKTOP_BREAKPOINT;
window.isMediumDesktop = () => window.innerWidth <= window.MEDIUM_DESKTOP_BREAKPOINT;
window.isWide = () => window.innerWidth > window.WIDE_BREAKPOINT;
window.isDesktop = () => window.innerWidth > window.TABLET_BREAKPOINT;
window.debounce = (fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn(...args), delay);
	};
};

/**
 * Parse le contenu JSON d'un <script type="application/json">. Tolérant aux erreurs.
 * @param {Element|string} target - Élément déjà résolu OU sélecteur CSS
 * @param {*} [fallback=null] - Valeur retournée si absent ou JSON invalide
 * @returns {*}
 */
window.parseJSON = (target, fallback = null) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	if (!el) return fallback;
	try {
		return JSON.parse(el.textContent);
	} catch (e) {
		return fallback;
	}
};

/**
 * Format un montant pour l'affichage, en répliquant le filtre Liquid `| money` de Shopify.
 * Utilise window.mmMoneyFormat (injecté dans mm-scripts.liquid depuis shop.money_format),
 * qui suit le Market/devise actif → rendu JS strictement identique au SSR.
 * @param {number} cents - Montant en centimes (ex: variant.price)
 * @param {boolean} [withCurrency=false] - Utiliser le format avec code devise (shop.money_with_currency_format)
 * @returns {string}
 */
window.formatMoney = (cents, withCurrency = false) => {
	const amount = (Number(cents) || 0) / 100;
	const format = (withCurrency ? window.mmMoneyWithCurrencyFormat : window.mmMoneyFormat) || '{{amount}}';
	// Réplique l'algorithme money_format de Shopify (placeholders {{amount...}})
	const formatNumber = (value, precision, thousands, decimal) => {
		const fixed = (Number(value) || 0).toFixed(precision);
		const [int, frac] = fixed.split('.');
		const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
		return frac ? `${withThousands}${decimal}${frac}` : withThousands;
	};
	const placeholders = {
		amount: (v) => formatNumber(v, 2, ',', '.'),
		amount_no_decimals: (v) => formatNumber(v, 0, ',', '.'),
		amount_with_comma_separator: (v) => formatNumber(v, 2, '.', ','),
		amount_no_decimals_with_comma_separator: (v) => formatNumber(v, 0, '.', ','),
		amount_with_apostrophe_separator: (v) => formatNumber(v, 2, "'", '.'),
		amount_with_space_separator: (v) => formatNumber(v, 2, ' ', ','),
		amount_no_decimals_with_space_separator: (v) => formatNumber(v, 0, ' ', ''),
	};
	return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
		placeholders[key] ? placeholders[key](amount) : match
	);
};

/*********************************************************************************************
 * 🔖 ASSETS VERSION — cache-buster des fetch de sections
 * Les réponses ?section_id= sont cachées à l'edge Shopify : juste après un déploiement, un
 * fetch peut recevoir une section PRÉ-déploiement (ancien schéma JSON, ex colors-data sans
 * "stock") et fausser les merges côté JS. Le checksum du bundle (?v= de mm_utils.js) change
 * à chaque déploiement → l'utiliser en query param force une entrée de cache fraîche.
 */
window.mmAssetsV = (document.currentScript && (document.currentScript.src.match(/[?&]v=([\w.]+)/) || [])[1]) || '';

/**
 * Produits > 250 variants : fetch les pages 2+ BRUTES de la section paginée
 * mm-product-variants-page (Shopify cappe product.variants ET /products/{handle}.js à 250).
 * Retourne les payloads JSON par page ({page, pages, total, variants, colors, mediaIds,
 * mediaData, infoModele}), pages en échec filtrées. Cache de Promise par handle — un seul
 * réseau pour tous les consommateurs (couleurs card/QAP via mmFetchColorPages, matrice
 * variants du QAP via _fetchVariantsData).
 * @param {string} handle - Handle produit
 * @param {number|null} variantsCount - product.variants_count ; null = inconnu → découverte
 *   (fetch page 2 puis 3..pages depuis son champ "pages")
 * @returns {Promise<Array>}
 */
window.mmFetchVariantPages = (() => {
	const cache = new Map();
	return (handle, variantsCount) => {
		if (!handle) return Promise.resolve([]);
		if (variantsCount !== null && !(variantsCount > 250)) return Promise.resolve([]);
		if (cache.has(handle)) return cache.get(handle);

		const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
		const fetchPage = (page) =>
			fetch(`${root}products/${handle}?section_id=mm-product-variants-page&page=${page}&_v=${window.mmAssetsV}`)
				.then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.text(); })
				.then(html => {
					const doc = new DOMParser().parseFromString(html, 'text/html');
					return window.parseJSON(doc.querySelector('.mm-variants-page-data'), null);
				})
				.catch(err => { window.log('warn', '🎨 [VARIANT_PAGES]', `page ${page} failed`, { handle, error: err.message }); return null; });

		let promise;
		if (variantsCount > 250) {
			const totalPages = Math.ceil(variantsCount / 250);
			const pages = [];
			for (let p = 2; p <= totalPages; p++) pages.push(p);
			promise = Promise.all(pages.map(fetchPage));
		} else {
			// Total inconnu (ex : card wishlist sans data-variants-count) : découverte via page 2.
			promise = fetchPage(2).then(d2 => {
				if (!d2) return [];
				const rest = [];
				for (let p = 3; p <= (d2.pages || 2); p++) rest.push(p);
				return Promise.all(rest.map(fetchPage)).then(ds => [d2, ...ds]);
			});
		}
		promise = promise.then(results => results.filter(Boolean));
		cache.set(handle, promise);
		return promise;
	};
})();

/**
 * Produits > 250 variants : agrège les pages 2+ par COULEUR au schéma des cards :
 * {name, style, img, available, coloriImgs, qty, id, stock, livraison:{qty,policy,onDemand}}.
 * - stock = cumul physique de la couleur (tri stock global), qty = variant représentatif.
 * - img / coloriImgs extraits de mediaData (featured + galerie metaobject colori).
 * Cache de Promise par handle (partagé quick-add / grille / panel). Résout [] en erreur.
 * @param {string} handle - Handle produit
 * @param {number} variantsCount - product.variants_count (déclenche les pages 2..N)
 * @returns {Promise<Array>}
 */
window.mmFetchColorPages = (() => {
	const cache = new Map();
	// mediaData émet le src natif Liquid : parfois relatif ("files/x.jpg") — inutilisable en
	// <img src>. Normaliser au format des cards SSR : /cdn/shop/… + width (cf. image_url: 800).
	const cdnSrc = (src, width) => {
		if (!src) return '';
		let url = src;
		if (!/^(https?:)?\/\//.test(url) && url[0] !== '/') url = '/cdn/shop/' + url;
		if (width && !/[?&]width=/.test(url)) url += (url.includes('?') ? '&' : '?') + 'width=' + width;
		return url;
	};
	return (handle, variantsCount) => {
		if (!handle || !(variantsCount > 250)) return Promise.resolve([]);
		if (cache.has(handle)) return cache.get(handle);

		const promise = window.mmFetchVariantPages(handle, variantsCount).then(results => {
			const colors = [];
			for (const data of results) {
				if (!data || !Array.isArray(data.colors)) continue;
				const variants = Array.isArray(data.variants) ? data.variants : [];
				const mediaData = data.mediaData || {};
				// mediaIds.featured = média RÉELLEMENT propre au variant. mediaData, lui, retombe
				// sur toute product.media quand la couleur n'a ni image dédiée ni metaobject colori
				// (fallback voulu pour la galerie PDP) : y prendre media[0] attribuait à la couleur
				// l'image d'un AUTRE coloris (ex "vert anglais" de Solange affiché en corail).
				const mediaIds = data.mediaIds || {};
				// Index de l'option couleur : déduit du variant représentatif de la 1ère couleur
				// (généralement 0, mais jamais supposé).
				let colorIdx = 0;
				const sample = data.colors[0];
				const sampleVariant = sample && variants.find(x => x.id === sample.id);
				if (sampleVariant?.options) {
					const idx = sampleVariant.options.indexOf(sample.name);
					if (idx >= 0) colorIdx = idx;
				}
				// Agrégats par couleur depuis les variants de la page.
				const agg = {};
				for (const v of variants) {
					const c = (v.options || [])[colorIdx];
					if (c == null) continue;
					const a = agg[c] = agg[c] || { stock: 0, available: false, policyContinue: false, onDemand: false, qty: 0 };
					a.stock += v.inventory_quantity > 0 ? v.inventory_quantity : 0;
					a.qty += v.inventory_quantity || 0;
					if (v.available) a.available = true;
					if (v.policy === 'continue') a.policyContinue = true;
					if (v.onDemand) a.onDemand = true;
				}
				for (const c of data.colors) {
					const a = agg[c.name] || { stock: 0, available: false, policyContinue: false, onDemand: false, qty: 0 };
					const media = (mediaData[String(c.id)] || {}).media || [];
					const repQty = (variants.find(v => v.id === c.id) || {}).inventory_quantity || 0;
					// onDemand émis AUSSI au 1er niveau : les consommateurs (pastilles grille/QAP,
					// injection du filtre couleur) lisent `c.onDemand`, pas `c.livraison.onDemand`.
					// Sans ça, une couleur "à la commande" des pages 2+ passait pour une couleur
					// normale (undefined = falsy) — d'où des pastilles qui ne devaient pas s'afficher.
					// La section paginée l'émet par couleur (c.onDemand) ; l'agrégat variants (a)
					// sert de repli quand le champ manque.
					// Le stock physique prime (ticket 86cb6mqhc) : une couleur qui a du stock
					// cumulé n'est jamais "à la commande", même si le metafield est resté à true.
					const colorOnDemand = (c.onDemand === true || a.onDemand === true) && a.stock <= 0;
					// Pas de featured ET pas de galerie colori dédiée → media[] est le fallback
					// product.media : aucune image ne représente CETTE couleur, on n'en propose
					// aucune (la card affiche alors "visuel non disponible", comme en SSR).
					const ownMedia = mediaIds[String(c.id)] || {};
					const hasOwnImage = ownMedia.featured != null || (ownMedia.extraCount || 0) > 0;
					colors.push({
						name: c.name,
						style: c.style || '',
						img: hasOwnImage ? cdnSrc(media[0]?.src, 800) : '',
						group: c.group || '',
						available: a.available,
						onDemand: colorOnDemand,
						coloriImgs: hasOwnImage ? media.slice(1).map(m => cdnSrc(m.src, 800)) : [],
						qty: repQty,
						id: c.id,
						stock: a.stock,
						livraison: { qty: Math.max(0, a.qty), policy: a.policyContinue ? 'continue' : 'deny', onDemand: colorOnDemand },
					});
				}
			}
			return colors;
		});

		cache.set(handle, promise);
		return promise;
	};
})();

/*********************************************************************************************
 * 🕐 TIMESTAMP UTILITIES
 */
// French timestamp formatter
window.getTimestamp = () => new Date().toLocaleString('fr-FR', {
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit'
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Share URL Select on Click (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-share-url]').forEach((input) => {
		input.addEventListener('click', () => {
			input.select();
		});
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	const eventConfig = {
		// Navigation & Menu events
		'mobile-menu-toggled': '📱',
		'mobile-menu-closed': '📱',
		'mobile-submenu-next': '📱',
		'mobile-submenu-prev': '📱',
		'background-filter-activated': '🌫️',
		'background-filter-deactivated': '🌫️',
		'mega-menu-products-switched': '🗂️',

		// Search events
		'search-opened': '🔍',
		'search-closed': '🔍',

		// UI Components events
		'dropdown-toggled': '📖',
		'read-more-toggled': '📝',
		'document-height-updated': '📐',
		'splide-initialized': '🎠',
		'loader-activated': '⏳',

		// Media events
		'video-manager-initialized': '🎥',

		// Recommendations events
		'product-recommendations-loaded': '🛍️',
		'product-recommendations-loading': '🛍️',
	};

	Object.entries(eventConfig).forEach(([event, emoji]) => {
		document.addEventListener(`global:${event}`, e =>
			console.log(`${emoji} [${event.toUpperCase()}]:`, e.detail)
		);
	});

	// Cart events (cart: prefix)
	const cartEvents = ['opened', 'closed', 'quantity-increased', 'quantity-decreased', 'free-product-adding', 'count-updated', 'add-to-cart-start', 'add-to-cart-success', 'add-to-cart-error'];
	cartEvents.forEach(event => {
		document.addEventListener(`cart:${event}`, e =>
			console.log(`🛒 [CART ${event.toUpperCase()}]:`, e.detail)
		);
	});
}